import type {
  EmailDraftView,
  MailAddressValue,
  RecipientCheck,
  RequestEmailSendResult,
} from '@fos/shared/contracts/mail';
import { MAIL_MESSAGES, MAIL_SEND_CHECK_TEXTS } from '@fos/shared/contracts/mail';
import { RECIPIENT_CHECK_LABELS } from '@fos/shared/labels';
import { Send } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { ContextBanner, FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { Skeleton } from '../../../ui/States';
import { Status } from '../../../ui/Status';
import { toast } from '../../../ui/Toast';
import { ClientPicker } from '../../clients/components/ClientPicker';
import { useMailRefresh } from '../hooks';
import { addressesText, parseAddressInput, recipientWarning } from '../mail-format';
import { AiDraftNotes } from './AiDraftNotes';

export interface ComposerDefaults {
  to?: MailAddressValue[];
  cc?: MailAddressValue[];
  subject?: string;
  bodyText?: string;
}

export interface ComposerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** edycja istniejącego szkicu (np. „Edytuj” w wątku albo szkic z Copilota) */
  draftId?: string | null;
  /** odpowiedź w wątku (klient i skrzynka z wątku) */
  threadId?: string | null;
  replyToMessageId?: string | null;
  /** klient wiadomości (w wątku: klient wątku; nowa wiadomość: wybór w formularzu, gdy brak) */
  clientId?: string | null;
  defaults?: ComposerDefaults;
  title?: string;
  /** po wysłaniu (od razu) albo wysłaniu do Akceptacji */
  onRequested?: (result: RequestEmailSendResult) => void;
}

function useDebounced<T>(value: T, ms = 400): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

function recipientsFromError(err: unknown): RecipientCheck[] {
  const details = (err as { data?: { details?: { recipients?: unknown } } } | null)?.data?.details;
  return Array.isArray(details?.recipients) ? (details.recipients as RecipientCheck[]) : [];
}

/**
 * Kompozytor maila (nowa wiadomość albo odpowiedź w wątku): Do, DW, temat, treść. Zapis jako szkic (`mail.createDraft`
 * / `mail.updateDraft`), wysyłka = `mail.requestSend`: osoba z „Wysyła maile” (i Akceptacjami do edycji) widzi „Wyślij”
 * — zgoda zapisuje się w tym samym żądaniu i mail wychodzi od razu; inni „Wyślij do akceptacji” (DECISIONS 2026-10-09).
 * Odpowiedź w wątku bez klienta: osoba z zakresem „Wszyscy klienci” może ją wysłać po zaznaczeniu „Wyślij bez klienta”.
 * Odbiorca musi być jednoznaczny (Etap 5): adres spoza kontaktów klienta, kontakt innego klienta albo kilku klientów
 * = ostrzeżenie i blokada do jawnego potwierdzenia odbiorcy (pole wyboru przy adresie). Edycja szkicu czekającego
 * w Akceptacjach wycofuje zgodę (backend).
 */
export function Composer({
  open,
  onOpenChange,
  draftId = null,
  threadId = null,
  replyToMessageId = null,
  clientId: clientIdProp = null,
  defaults,
  title,
  onRequested,
}: ComposerProps) {
  const refresh = useMailRefresh();
  const access = useAccess();
  // tylko etykieta i opis (backend sam rozstrzyga: wynik `approvedNow`)
  const canSendNow = access.can('approvals', 'edit') && access.flag('sendsEmails');
  const canSendWithoutClient = access.dataScope === 'all';
  const [withoutClient, setWithoutClient] = useState(false);
  const [draft, setDraft] = useState<EmailDraftView | null>(null);
  const [toText, setToText] = useState('');
  const [ccText, setCcText] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [clientId, setClientId] = useState<string | null>(clientIdProp);
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const [serverChecks, setServerChecks] = useState<RecipientCheck[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const initialized = useRef<string | null>(null);

  const existing = trpc.mail.getDraft.useQuery({ id: draftId ?? '' }, { enabled: open && Boolean(draftId) });

  // wartości początkowe: szkic albo domyślne (raz na otwarcie / szkic)
  useEffect(() => {
    if (!open) {
      initialized.current = null;
      return;
    }
    const key = draftId ?? `new:${threadId ?? ''}:${replyToMessageId ?? ''}`;
    if (initialized.current === key) return;
    if (draftId) {
      if (!existing.data) return;
      const d = existing.data;
      setDraft(d);
      setToText(addressesText(d.to));
      setCcText(addressesText(d.cc));
      setSubject(d.subject);
      setBody(d.bodyText);
      setClientId(d.clientId);
    } else {
      setDraft(null);
      setToText(addressesText(defaults?.to ?? []));
      setCcText(addressesText(defaults?.cc ?? []));
      setSubject(defaults?.subject ?? '');
      setBody(defaults?.bodyText ?? '');
      setClientId(clientIdProp);
    }
    setConfirmed(new Set());
    setWithoutClient(false);
    setServerChecks([]);
    setErrors({});
    setFormError(null);
    initialized.current = key;
  }, [open, draftId, threadId, replyToMessageId, existing.data, defaults, clientIdProp]);

  const to = useMemo(() => parseAddressInput(toText), [toText]);
  const cc = useMemo(() => parseAddressInput(ccText), [ccText]);
  const effectiveClient = clientId ?? clientIdProp;
  // odpowiedź w wątku bez klienta (nierozpoznany / niejednoznaczny nadawca): szkic tak, wysyłka po przypisaniu wątku
  const noClientThread = Boolean(threadId || draft?.threadId) && !effectiveClient;
  // „Wyślij bez klienta” (świadome potwierdzenie) odblokowuje wysyłkę tylko osobie z zakresem „Wszyscy klienci”
  const missingClient = noClientThread && !(canSendWithoutClient && withoutClient);
  const checkInput = useDebounced({ clientId: effectiveClient, to: to.list, cc: cc.list });
  const checks = trpc.mail.checkRecipients.useQuery(checkInput, {
    enabled: open && checkInput.to.length + checkInput.cc.length > 0,
    staleTime: 15_000,
  });
  const flagged = useMemo(() => {
    const list = (checks.data ?? []).filter((c) => c.needsConfirmation);
    for (const s of serverChecks) if (!list.some((c) => c.email === s.email)) list.push(s);
    // bez klienta każdy adres jest „nowy” — potwierdza go „Wyślij bez klienta”; kontakt klienta nadal do potwierdzenia
    return noClientThread ? list.filter((c) => c.status !== 'new_address') : list;
  }, [checks.data, serverChecks, noClientThread]);

  const create = trpc.mail.createDraft.useMutation();
  const update = trpc.mail.updateDraft.useMutation();
  const request = trpc.mail.requestSend.useMutation();
  const busy = create.isPending || update.isPending || request.isPending;

  function validate(forSend: boolean): boolean {
    const next: Record<string, string> = {};
    if (to.invalid.length) next.to = `Nieprawidłowy adres: ${to.invalid.join(', ')}`;
    if (cc.invalid.length) next.cc = `Nieprawidłowy adres: ${cc.invalid.join(', ')}`;
    if (forSend) {
      if (to.list.length === 0 && !next.to) next.to = 'Podaj odbiorcę';
      if (!subject.trim()) next.subject = MAIL_MESSAGES.subjectRequired;
      if (!body.trim()) next.bodyText = MAIL_MESSAGES.bodyRequired;
      if (!threadId && !effectiveClient) next.clientId = 'Wybierz klienta';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function save(): Promise<EmailDraftView> {
    const fields = { to: to.list, cc: cc.list, subject, bodyText: body };
    if (draft) {
      const saved = await update.mutateAsync({ id: draft.id, version: draft.version, ...fields });
      setDraft(saved);
      return saved;
    }
    const created = await create.mutateAsync({
      threadId,
      replyToMessageId,
      clientId: threadId ? null : effectiveClient,
      ...fields,
    });
    setDraft(created);
    return created;
  }

  function handleError(err: unknown, fallback: string) {
    if (appErrorCode(err) === 'CONFLICT') {
      setFormError('Szkic zmienił się w międzyczasie (albo czeka już w Akceptacjach). Zamknij i otwórz go ponownie.');
      return;
    }
    const recipients = recipientsFromError(err);
    if (recipients.length) {
      setServerChecks(recipients);
      setFormError(MAIL_MESSAGES.recipientsNeedConfirmation);
      return;
    }
    const issues = validationIssues(err);
    if (issues.length) {
      setErrors(Object.fromEntries(issues.map((i) => [i.path.split('.')[0] ?? i.path, i.message])));
      setFormError(issues.some((i) => i.path === 'clientId') ? (issues[0]?.message ?? null) : null);
      return;
    }
    setFormError(errorMessage(err, fallback));
  }

  async function onSaveDraft() {
    setFormError(null);
    if (!validate(false)) return;
    try {
      await save();
      await refresh();
      toast.success('Szkic zapisany');
      onOpenChange(false);
    } catch (err) {
      handleError(err, 'Nie udało się zapisać szkicu.');
    }
  }

  async function onSend() {
    setFormError(null);
    if (!validate(true)) return;
    // baner nad formularzem mówi, co zrobić (przycisk wysyłki jest wtedy wyłączony)
    if (missingClient) return;
    const missing = flagged.filter((c) => !confirmed.has(c.email));
    if (missing.length > 0) {
      setFormError(MAIL_MESSAGES.recipientsNeedConfirmation);
      return;
    }
    try {
      const saved = await save();
      const result = await request.mutateAsync({
        id: saved.id,
        version: saved.version,
        confirmRecipients: [...confirmed],
        ...(noClientThread && withoutClient ? { withoutClient: true } : {}),
      });
      await refresh();
      if (result.approvedNow) {
        toast.success(MAIL_MESSAGES.sentNow, {
          description: result.simulated ? MAIL_MESSAGES.sentNowSimulated : undefined,
        });
      } else {
        toast.success(MAIL_MESSAGES.queuedForApproval, {
          description: result.simulated ? 'Symulacja — po zatwierdzeniu nic nie zostanie naprawdę wysłane.' : undefined,
        });
      }
      onRequested?.(result);
      onOpenChange(false);
    } catch (err) {
      handleError(err, canSendNow ? 'Nie udało się wysłać maila.' : 'Nie udało się wysłać maila do akceptacji.');
    }
  }

  const loading = Boolean(draftId) && existing.isPending;
  const pendingWarning = draft?.status === 'pending' && !draft.sendOutcomeUnknown;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title={title ?? (threadId ? 'Odpowiedź' : 'Nowa wiadomość')}
      description={
        canSendNow
          ? 'Mail wyjdzie po kliknięciu „Wyślij”.'
          : 'Mail trafi do Akceptacji — nic nie wyjdzie bez zgody osoby z uprawnieniem „Wysyła maile”.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Anuluj
          </Button>
          <Button variant="secondary" onClick={() => void onSaveDraft()} loading={create.isPending || update.isPending}>
            Zapisz szkic
          </Button>
          <Button
            variant="primary"
            icon={Send}
            onClick={() => void onSend()}
            loading={request.isPending}
            disabled={missingClient}
          >
            {canSendNow ? 'Wyślij' : 'Wyślij do akceptacji'}
          </Button>
        </>
      }
    >
      {loading ? (
        <div className="flex flex-col gap-3" role="status" aria-label="Wczytywanie szkicu">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : existing.isError && draftId ? (
        <FormMessage>{errorMessage(existing.error, 'Nie udało się wczytać szkicu.')}</FormMessage>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void onSend();
          }}
        >
          {formError && <FormMessage>{formError}</FormMessage>}
          {noClientThread && !canSendWithoutClient && (
            <ContextBanner tone="warning">
              Wątek nie ma klienta. Przypisz go do klienta, zanim wyślesz odpowiedź — szkic możesz zapisać już teraz.
            </ContextBanner>
          )}
          {noClientThread && canSendWithoutClient && (
            <ContextBanner tone="warning">
              <span className="flex flex-col gap-2">
                <span>Wątek nie ma klienta. Przypisz go do klienta albo wyślij odpowiedź świadomie bez klienta.</span>
                <Checkbox
                  checked={withoutClient}
                  onCheckedChange={(v) => setWithoutClient(v === true)}
                  label="Wyślij bez klienta"
                />
              </span>
            </ContextBanner>
          )}
          {draft?.ai && <AiDraftNotes notes={draft.ai} />}
          {draft?.sendOutcomeUnknown && (
            <ContextBanner tone="warning">{MAIL_SEND_CHECK_TEXTS[draft.sendCheck ?? 'sent_folder'].hint}</ContextBanner>
          )}
          {pendingWarning && (
            <ContextBanner tone="warning">
              Ten szkic czeka w Akceptacjach. Zmiana wycofa poprzednią zgodę — trzeba będzie wysłać go ponownie.
            </ContextBanner>
          )}
          {!threadId && !draft?.threadId && (
            <Field label="Klient" error={errors.clientId}>
              <ClientPicker value={effectiveClient} onChange={setClientId} excludePartial />
            </Field>
          )}
          <Field label="Do" error={errors.to} hint="Kilka adresów oddziel przecinkiem.">
            <Input
              value={toText}
              onChange={(e) => setToText(e.target.value)}
              placeholder="anna@rea.pl"
              autoComplete="off"
              inputMode="email"
            />
          </Field>
          <Field label="DW" error={errors.cc}>
            <Input value={ccText} onChange={(e) => setCcText(e.target.value)} autoComplete="off" inputMode="email" />
          </Field>
          {flagged.length > 0 && !missingClient && (
            <section
              aria-label="Odbiorcy do potwierdzenia"
              className="flex flex-col gap-2 rounded-lg bg-warning-subtle px-4 py-3"
            >
              <p className="text-body-s font-medium text-fg">Potwierdź odbiorców przed wysyłką</p>
              {flagged.map((c) => (
                <div key={c.email} className="flex flex-col gap-1">
                  <span className="flex flex-wrap items-center gap-2 text-body-s text-fg-secondary">
                    <Status tone="warning">{RECIPIENT_CHECK_LABELS[c.status]}</Status>
                    <span className="min-w-0 break-words">{recipientWarning(c)}</span>
                  </span>
                  <Checkbox
                    checked={confirmed.has(c.email)}
                    onCheckedChange={(v) =>
                      setConfirmed((prev) => {
                        const next = new Set(prev);
                        if (v) next.add(c.email);
                        else next.delete(c.email);
                        return next;
                      })
                    }
                    label={`Potwierdzam odbiorcę ${c.email}`}
                  />
                </div>
              ))}
            </section>
          )}
          <Field label="Temat" error={errors.subject}>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={998} />
          </Field>
          <Field label="Treść" error={errors.bodyText}>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} />
          </Field>
          <p className="text-caption text-fg-muted">
            {MAIL_MESSAGES.sendIrreversible} Bez załączników; podpis skrzynki (jeśli ustawiony) dodamy pod treścią.
          </p>
        </form>
      )}
    </Dialog>
  );
}
